import { ResponseType, ShowOn } from '../../../../types/helios/Question';
import { isPassFailSub } from '../../../shared/domain/subQuestionResponseType';
import {
  subQuestionHasViolation,
  type SubQuestionViolation,
} from '../../../shared/domain/subQuestionValidation';
import type { SubQuestion } from './reviewTypes';

/**
 * Pure helpers for {@link SubQuestionsStep}, extracted so the component stays
 * under the size limit and these decisions are unit-testable without rendering.
 */

/**
 * A fresh sub-question. Normally Pass/Fail with the four requirement toggles
 * off. When conditional display is ON, a Pass/Fail sub is INVALID (a scored sub
 * can't be conditional — it would break the "every sub is data-collection"
 * invariant the toggle enforces), so seed a data-collection Text sub with a
 * default `showWhenParentIs: BOTH` trigger instead. This keeps a newly-added
 * row valid for publish without the author having to fix its type first.
 */
export function createEmptySubQuestion(
  orderIndex: number,
  conditionalEnabled: boolean,
): SubQuestion {
  if (conditionalEnabled) {
    return {
      title: '',
      instruction: '',
      orderIndex,
      responseType: ResponseType.TEXT,
      showWhenParentIs: ShowOn.BOTH,
      requireComment: false,
      requireAttachment: false,
    };
  }
  return {
    title: '',
    instruction: '',
    orderIndex,
    requireCommentOnFail: false,
    requireEvidenceOnFail: false,
    requireCommentOnPass: false,
    requireEvidenceOnPass: false,
  };
}

/** Next orderIndex = one past the current max (or 1 for the first). */
export function nextOrderIndex(subs: readonly SubQuestion[]): number {
  return subs.reduce((max, sq) => (sq.orderIndex > max ? sq.orderIndex : max), 0) + 1;
}

/**
 * Whether a row shows the single warning marker: the conditional-display block
 * (a Pass/Fail sub while blocked) OR a publish violation (e.g. a select type
 * with fewer than two options). Both surface as one marker; the banners carry
 * the specific messages. `index` is 0-based (violations use 1-based row
 * numbers).
 */
export function rowHasIssue(
  sub: SubQuestion,
  index: number,
  blocked: boolean,
  violations: readonly SubQuestionViolation[],
): boolean {
  return (
    (blocked && isPassFailSub(sub)) || subQuestionHasViolation(violations, index + 1)
  );
}
